import { newElementWith } from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import type { ExcalidrawElement, GroupId } from "@excalidraw/element/types";

import { register } from "./register";

export const getGroupName = (
  elements: readonly ExcalidrawElement[],
  groupId: GroupId,
): string | null => {
  for (const element of elements) {
    const name = element.customData?.groupNames?.[groupId];
    if (name) {
      return name;
    }
  }
  return null;
};

export const actionToggleElementHidden = register({
  name: "toggleElementHidden",
  label: "labels.layersPanel.toggleHidden",
  trackEvent: { category: "element" },
  perform: (elements, appState, formData, app) => {
    // formData is contextually unknown (register can't infer TData from an
    // annotated param) — cast to the shape executeAction callers pass
    const data = formData as { ids?: string[] } | null | undefined;
    const targetIds = new Set(
      data?.ids ??
        app.scene
          .getSelectedElements({
            selectedElementIds: appState.selectedElementIds,
            includeBoundTextElement: true,
            includeElementsInFrames: true,
          })
          .map((element) => element.id),
    );
    if (!targetIds.size) {
      return false;
    }
    const targets = elements.filter((element) => targetIds.has(element.id));
    const nextHidden = targets.some((element) => !element.hidden);

    const nextSelectedElementIds = { ...appState.selectedElementIds };
    if (nextHidden) {
      for (const id of targetIds) {
        delete nextSelectedElementIds[id];
      }
    }
    return {
      elements: elements.map((element) =>
        targetIds.has(element.id)
          ? newElementWith(element, { hidden: nextHidden })
          : element,
      ),
      appState: { ...appState, selectedElementIds: nextSelectedElementIds },
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
});

export const actionRenameElement = register({
  name: "renameElement",
  label: "labels.layersPanel.rename",
  trackEvent: { category: "element" },
  perform: (elements, appState, formData) => {
    const data = formData as
      | { elementId?: string; groupId?: GroupId; name: string }
      | undefined;
    if (!data || typeof data.name !== "string") {
      return false;
    }
    const name = data.name.trim() || null;

    if (data.groupId) {
      const groupId = data.groupId;
      return {
        elements: elements.map((element) => {
          if (!element.groupIds.includes(groupId)) {
            return element;
          }
          const groupNames = { ...element.customData?.groupNames };
          if (name === null) {
            delete groupNames[groupId];
          } else {
            groupNames[groupId] = name;
          }
          return newElementWith(element, {
            customData: { ...element.customData, groupNames },
          });
        }),
        appState,
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      };
    }

    return {
      elements: elements.map((element) =>
        element.id === data.elementId
          ? newElementWith(element, { name })
          : element,
      ),
      appState,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
});
